/**
 * A utility class for building query strings in a fluent and type-safe manner.
 * It provides methods to add parameters, set parameters, and convert the builder to a query string or full URL.
 * It handles invalid values by skipping them, ensuring that the resulting query string is clean and does not contain unintended keys or values.
 */
export class QueryBuilder {
  private params = new URLSearchParams();

  constructor(obj?: object) {
    if (obj) {
      this.fromObject(obj);
    }
  }

  private fromObject(obj: object) {
    for (const [key, value] of Object.entries(obj)) {
      this.add(key, value);
    }
  }

  private isValidValue(value: any) {
    // Will be key=undefined, which will be ignored by the server, and will possibly introduce bugs.
    // E.g. if a search value is undefined, it will be added as key=undefined, and the server will not ignore it, but will search for items with the value "undefined".
    if (value === undefined) return false;

    // Objects are skipped as they cannot be reliably converted to a string representation.
    // This is a safeguard against accidentally passing complex objects, which would result in [object Object] in the query string.
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      return false;
    }

    return true;
  }

  /**
   * Adds a query parameter to the builder. If the value is undefined, the parameter will be skipped.
   * Arrays will be added as comma-separated values.
   * Objects are dropped as they cannot be reliably converted to a string representation.
   *
   * If you need multiple keys for array values use: addArray()
   *
   * @param key name of the key
   * @param value the data to be added, if undefined the key will not be added to the query string
   * @returns the current instance of QueryBuilder for chaining
   */
  add(key: string, value: any) {
    if (!this.isValidValue(value)) return this;

    // All other values are added as strings. Arrays will be converted to comma-separated values by URLSearchParams.
    this.params.append(key, value ?? '');

    return this;
  }

  /**
   * Sets a query parameter to the builder. If the value is undefined, the parameter will be skipped.
   * If the key already exists, it will be overwritten with the new value.
   * Arrays will be added as comma-separated values.
   * Objects are dropped as they cannot be reliably converted to a string representation.
   *
   * If you need multiple keys for array values use: addArray()
   * @param key name of the key
   * @param value the data to be set.
   * @returns the current instance of QueryBuilder for chaining
   */
  set(key: string, value: any) {
    if (!this.isValidValue(value)) return this;

    this.params.set(key, value ?? '');
    return this;
  }

  /**
   * Adds multiple query parameters with the same key. Each value in the array will be added as a separate key-value pair.
   * E.g. addArray('id', [1,2,3]) will result in ?id=1&id=2&id=3
   * Skip if the array is empty or undefined.
   * Array is cleaned before adding, only primitive values are added, objects and undefined values are skipped.
   *
   * @param key name of the key
   * @param values the array values to add.
   * @returns the current instance of QueryBuilder for chaining
   */
  addArray(key: string, values: any[]) {
    if (!values || values.length === 0) return this;

    for (const item of values) {
      this.add(key, item);
    }

    return this;
  }

  /**
   * Converts the added parameters to a query string. If no parameters have been added, an empty string is returned.
   *
   * @returns the query string - empty string of no params
   */
  toString() {
    const queryString = this.params.toString();
    return queryString ? `?${queryString}` : '';
  }

  /**
   * Converts the added parameters to a full URL. If no parameters have been added, the original URL is returned.
   *
   * @returns the full URL with query parameters
   */
  forUrl(url: string) {
    const params = this.toString();
    return url ? `${url}${params}` : params;
  }
}
